import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  searchCateringCustomerAsync,
  createCateringCustomerAsync,
} from "../../redux/features/cateringCustomer/cateringCustomerThunks";
import { CreateAccount } from "../../Screens/registerAccount";
import {
  selectCateringIsUserSearched,
  selectCateringSearchedCustomer,
  selectCateringSelectedAddress,
} from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import { CateringAddressSelection } from "./CateringAddressSelection";
import { selectAreas } from "../../redux/features/country/countrySelectors";
import { AvailableBrands } from "../../components/availableBrands";
import { selectCateringBrandsList } from "../../redux/features/restaurants/restaurantSelectors";
import {
  setIsUserSearched,
  clearSearchedCustomer,
  setPastOrders,
  setSelectedAddress,
  clearAllCateringCustomerData,
} from "../../redux/features/cateringCustomer/cateringCustomerSlice";
import { clearAllCateringData, clearPastOrderAddressId } from "../../redux/features/restaurants/restaurantSlice";
import { getAllCateringRestaurantsAsync } from "../../redux/features/restaurants/restaurantThunks";
import { selectPastOrderAddressId } from "../../redux/features/restaurants/restaurantSelectors";
import CateringAddNewAddress from "./CateringAddNewAddress";
import PastOrders from "../pastOrders";
import CateringPastOrders from "../pastOrders/CateringPastOrders";
import EditUserModal from "../userSearch/EditUserModal";
import { transformAddresses } from "../../utils/addresses";

// Validation schema using Yup
const validationSchema = Yup.object({
  phoneNumber: Yup.string()
    .matches(/^\d{8}$/, "Phone number must be exactly 8 digits")
    .required("Phone number is required"),
});

export const IndividualCatering = ({ isCheckout = false, setShowAddressForm }) => {
  const dispatch = useDispatch();
  
  // Get customer data from Redux state
  const customerData = useSelector(selectCateringSearchedCustomer);
  const isUserSearched = useSelector(selectCateringIsUserSearched);
  const areas = useSelector(selectAreas);
  const selectedAddress = useSelector(selectCateringSelectedAddress);
  const branchesData = useSelector(selectCateringBrandsList);
  const pastOrderAddressId = useSelector(selectPastOrderAddressId);
  
  const [loading, setLoading] = useState(false);
  const [createNewAccount, setCreateNewAccount] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);
  const [userMobileNo, setUserMobileNo] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);

  // Handler for successful account creation
  const handleAccountCreated = () => {
    setCreateNewAccount(false);
    if (userMobileNo) {
      dispatch(searchCateringCustomerAsync("+965", userMobileNo));
    }
  };

  // Handle clearing customer data when X button is clicked
  const handleClearCustomerData = () => {
    formik.resetForm();
    dispatch(clearAllCateringCustomerData()); // Clear all catering customer data
    dispatch(clearAllCateringData()); // Clear all catering data
    setShowAddresses(false);
    setCreateNewAccount(false);
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      phoneNumber: "",
      firstName: "",
      lastName: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        setUserMobileNo(values.phoneNumber);
        dispatch(clearAllCateringData()); // Clear catering data when searching for new customer
        await dispatch(searchCateringCustomerAsync("+965", values.phoneNumber));
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (isUserSearched) {
      setCreateNewAccount(!customerData);
      setShowAddresses(!!customerData); // Convert to boolean explicitly
    }
  }, [dispatch, isUserSearched, customerData]);

  // Note: Past orders are now handled by the CateringPastOrders component
  // which uses the catering-specific API endpoints

  // Past orders are now handled by the CateringPastOrders component

  useEffect(() => {
    if (customerData) {
      formik.setValues({
        phoneNumber: customerData.mobile_no || "",
        firstName: customerData.first_name || "",
        lastName: customerData.last_name || "",
      });
      dispatch(setIsUserSearched(true));
    }
  }, [customerData]);

  // Load catering brands when an address is selected
  useEffect(() => {
    if (selectedAddress && (!branchesData || branchesData.length === 0)) {
      console.log("Loading catering brands for selected address:", selectedAddress);
      dispatch(getAllCateringRestaurantsAsync());
    }
  }, [selectedAddress, branchesData, dispatch]);

  // Check for past order address restoration - from Redux
  useEffect(() => {
    if (pastOrderAddressId && customerData && customerData.addresses && !selectedAddress) {
      console.log('Looking for address ID from past order (Redux):', pastOrderAddressId, 'in customer addresses:', customerData.addresses);
      
      // Find the matching address from customer's addresses
      const matchingAddress = customerData.addresses.find(addr => addr.id === pastOrderAddressId);
      
      if (matchingAddress) {
        // Use the same transformation logic as CateringAddressSelection
        const transformedAddresses = transformAddresses([matchingAddress]);
        const transformedAddress = transformedAddresses[0];
        
        console.log('Found matching address from past order:', transformedAddress);
        dispatch(setSelectedAddress(transformedAddress));
        
        // Clear the Redux flag after successful matching
        dispatch(clearPastOrderAddressId());
      } else {
        console.warn('Could not find matching address for past order address ID:', pastOrderAddressId);
      }
    }
  }, [customerData, selectedAddress, pastOrderAddressId, dispatch]);

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <div className="flex flex-col sm:flex-row sm:space-x-2 items-center">
          {/* Country Code */}
          <div className="mb-2 sm:mb-0">
            <input
              type="text"
              placeholder="+965 (KW)"
              readOnly
              className="border border-slate-300 bg-neutral-50 focus:ring-1 focus:ring-slate-300 focus:outline-none placeholder-gray-400 placeholder-bold px-4 py-2 w-full sm:w-[133px] h-12 rounded-xl"
            />
          </div>

          {/* Phone Number */}
          <div className="relative mb-2 sm:mb-0 w-full sm:w-[167px]">
            <input
              type="text"
              name="phoneNumber"
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isCheckout}
              placeholder="Enter Phone Number"
              className="border border-slate-300 bg-neutral-50 rounded-xl focus:ring-1 focus:ring-slate-300 focus:outline-none placeholder-gray-400 placeholder-bold text-gray-400 font-bold px-4 py-2 w-full sm:w-[167px] h-12"
            />
            {/* X icon to clear input */}
            {formik.values.phoneNumber && !isCheckout && (
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={handleClearCustomerData}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
            {/* Error message */}
            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
              <div className="absolute text-red-500 text-sm mt-1 left-0">
                {formik.errors.phoneNumber}
              </div>
            )}
          </div>

          {/* Search Button */}
          <div className="flex space-x-2">
            {!isCheckout && (
              <button
                type="submit"
                className="text-white font-bold bg-amber-800 rounded-xl h-12 w-[103px] focus:ring-1 focus:ring-amber-800 focus:outline-none"
                disabled={loading}
              >
                {loading ? "Searching..." : "Search"}
              </button>
            )}

            {/* Add New Address Button - show when customer is loaded */}
            {!isCheckout && customerData && <CateringAddNewAddress type="address" setShowAddressForm={setShowAddressForm} />}
          </div>
        </div>
      </form>
      
      {createNewAccount ? (
        <div className="mt-9">
          <CreateAccount
            mobileNumber={userMobileNo}
            onAccountCreated={handleAccountCreated}
            serviceType="individual catering"
            createCustomerThunk={createCateringCustomerAsync}
          />
        </div>
      ) : (
        isUserSearched && (
          <div className="mt-7 flex flex-wrap space-x-2 items-center">
            <input
              type="text"
              name="firstName"
              value={formik.values.firstName}
              readOnly
              className="border border-slate-300 bg-neutral-50 rounded-xl focus:ring-1 focus:ring-slate-300 focus:outline-none text-gray-400 font-bold px-4 py-2 w-full sm:w-[167px] h-12"
            />
            <input
              type="text"
              name="lastName"
              value={formik.values.lastName}
              readOnly
              className="border border-slate-300 bg-neutral-50 rounded-xl focus:ring-1 focus:ring-slate-300 focus:outline-none text-gray-400 font-bold px-4 py-2 w-full sm:w-[167px] h-12"
            />
            
            {/* Edit User Button */}
            {!isCheckout && (
              <button
                type="button"
                onClick={() => setIsEditUserModalOpen(true)}
                className="text-white font-bold bg-amber-800 rounded-xl h-12 w-[103px] focus:ring-1 focus:ring-amber-800 focus:outline-none"
              >
                Edit User
              </button>
            )}
          </div>
        )
      )}

      {/* EditUserModal Component */}
      {customerData && (
        <EditUserModal
          isOpen={isEditUserModalOpen}
          onClose={() => setIsEditUserModalOpen(false)}
          userData={customerData}
        />
      )}

      {/* Display selected address in checkout */}
      {isCheckout && selectedAddress && (
        <div className="mt-4">
          <h2 className="text-lg font-bold text-gray-500 mb-2">Catering Address</h2>
          <div className="min-w-[257px] max-w-[257px] w-[257px] h-[162px] border-2 border-amber-100 bg-neutral-50 p-4 rounded-xl flex flex-col justify-between">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-lg text-gray-500 truncate overflow-hidden">{selectedAddress.firstLine}</h3>
              {selectedAddress.active_order === "true" && (
                <button className="w-20 h-5 bg-emerald-600 text-white border border-white rounded-lg font-bold text-xs flex-shrink-0 ml-1">
                  Active Order
                </button>
              )}
            </div>
            <h4 className="text-gray-500 text-sm truncate">{selectedAddress.area}</h4>
            {selectedAddress.houseName && <h4 className="text-gray-500 text-sm truncate">{selectedAddress.houseName}</h4>}
            {selectedAddress.buildingNumber && <h5 className="text-gray-500 text-sm truncate">{selectedAddress.buildingNumber}</h5>}
            {selectedAddress.floor && <h6 className="text-gray-500 text-sm truncate">{selectedAddress.floor}</h6>}
            {selectedAddress.number && <p className="text-gray-500 text-sm truncate">{selectedAddress.number}</p>}
          </div>
        </div>
      )}

      {/* Only show these components if not in checkout */}
      {!isCheckout && customerData && (
        <div className="w-full flex gap-4 mt-4">
          {/* Left side: Address Selection and Past Orders - 50% width */}
          <div className="w-1/2 flex-shrink-0 space-y-4">
            {/* Address Selection */}
            <div>
              <CateringAddressSelection isCheckout={isCheckout} setShowAddressForm={setShowAddressForm} />
            </div>

            {/* Catering Past Orders - below address selection */}
            <div>
              <CateringPastOrders />
            </div>
          </div>

          {/* Right side: Available Brands - 50% width - only show when address is selected */}
          {branchesData && branchesData.length > 0 && selectedAddress && (
            <div className="w-1/2 flex-shrink-0">
              <AvailableBrands 
                branchesData={branchesData} 
              />
            </div>
          )}
        </div>
      )}

      {/* Address Form */}
      {/* {showAddressForm && <CateringAddressForm />} */}
    </>
  );
}; 
